import cx from 'classnames';
import { FormattedMessage } from 'react-intl';

import { EnterpriseIcon } from '@trello/nachos/icons/enterprise';

import { LazyWorkspaceShareLinkSettings } from 'app/src/components/InviteTeamMembers';
import { LazyWorkspaceUserLimitMessage } from 'app/src/components/WorkspaceUserLimitMessage';

import styles from './WorkspaceMembersInfoHeader.less';

export interface WorkspaceMembersInfoHeaderProps {
  canManageMembers: boolean;
  guestMemberCount: number;
  hasBC: boolean;
  isEnterpriseAdmin: boolean;
  isOrgManaged: boolean;
  isPaidWorkspace: boolean;
  orgIsPremiumPO: boolean;
  isStandard: boolean;
  memberTypeFilter: string;
  model: {
    belongsToRealEnterprise: () => boolean;
    getProduct: () => number | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    get: (key: string) => any;
  };
  orgId: string;
  orgMemberCount: number;
  requestMemberCount: number;
}

export const WorkspaceMembersInfoHeader = ({
  canManageMembers,
  guestMemberCount,
  hasBC,
  isEnterpriseAdmin,
  isOrgManaged,
  isPaidWorkspace,
  isStandard,
  memberTypeFilter,
  model,
  orgId,
  orgMemberCount,
  requestMemberCount,
  orgIsPremiumPO,
}: WorkspaceMembersInfoHeaderProps) => {
  const isWSAdminAllowedToInvite = !(
    model.get('prefs')?.newLicenseInviteRestrict === 'ent-admin'
  );
  let teamMembersView;
  if (
    model.belongsToRealEnterprise() &&
    (canManageMembers || isEnterpriseAdmin)
  ) {
    teamMembersView = (
      <FormattedMessage
        id="templates.org_members.team-members-view-enterprise"
        defaultMessage="Workspace members are a part of your Enterprise account. Adding a new person to your Enterprise will use a license seat."
        description="Team members view for enterprise"
      />
    );
  } else if ((hasBC || isStandard) && canManageMembers && !orgIsPremiumPO) {
    teamMembersView = (
      <FormattedMessage
        id="templates.org_members.team-members-view-bc"
        defaultMessage="Workspace members can view and join all Workspace visible boards and create new boards in the Workspace. Adding new members will automatically update your billing."
        description="Team members view for BC"
      />
    );
  } else {
    teamMembersView = (
      <FormattedMessage
        id="templates.org_members.team-members-view"
        defaultMessage="Workspace members can view and join all Workspace visible boards and create new boards in the Workspace."
        description="Team members view"
      />
    );
  }

  let guestsView;
  if (
    guestMemberCount &&
    model.belongsToRealEnterprise() &&
    (canManageMembers || isEnterpriseAdmin)
  ) {
    guestsView = (
      <>
        <FormattedMessage
          id="templates.org_members.guests-view"
          defaultMessage="Guests can only view and edit the boards to which they've been added."
          description="Guests view"
        />
        <br />
        <FormattedMessage
          id="templates.org_members.guests-view-enterprise"
          defaultMessage="Adding a guest as a member of your Workspace will add a new person to your Enterprise and will use a license seat."
          description="Guests view for enterprise"
        />
      </>
    );
  } else {
    guestsView = (
      <FormattedMessage
        id="templates.org_members.guests-view"
        defaultMessage="Guests can only view and edit the boards to which they've been added."
        description="Guests view"
      />
    );
  }

  return (
    <div>
      <div
        className={cx(styles.section, {
          [styles.active]: memberTypeFilter === 'team-member',
        })}
      >
        <div className={styles.container}>
          <h1>
            <FormattedMessage
              id="templates.org_members.workspace-members-count"
              defaultMessage="Workspace members ({count})"
              values={{ count: orgMemberCount }}
              description="Workspace members count"
            />
            {hasBC && <EnterpriseIcon size="small" />}
          </h1>
          <p>{teamMembersView}</p>
          <LazyWorkspaceUserLimitMessage
            location="members"
            workspaceId={orgId}
          />
          {isWSAdminAllowedToInvite && (
            <LazyWorkspaceShareLinkSettings
              idOrganization={orgId}
              isManaged={isOrgManaged}
              canManageMembers={canManageMembers}
            />
          )}
        </div>
      </div>
      <div
        className={cx(styles.section, {
          [styles.active]: memberTypeFilter === 'guest',
        })}
      >
        <div className={styles.container}>
          <h1>
            <FormattedMessage
              id="templates.org_members.guests-count"
              defaultMessage="Guests ({count})"
              values={{ count: guestMemberCount }}
              description="Guests count"
            />
          </h1>
          <p>{guestsView}</p>
          <LazyWorkspaceUserLimitMessage
            location="members"
            workspaceId={orgId}
          />
          {!guestMemberCount && (
            <div className={styles.guestEmptyState}>
              <hr role="presentation" />
              <p>
                <FormattedMessage
                  id="templates.org_members.no-guests"
                  defaultMessage="There are no guests in this Workspace."
                  description="No guests message"
                />
              </p>
            </div>
          )}
        </div>
      </div>
      <div
        className={cx(styles.section, {
          [styles.active]: memberTypeFilter === 'request',
        })}
      >
        <div className={styles.container}>
          <h1>
            <FormattedMessage
              id="templates.org_members.join-requests-count"
              defaultMessage="Join requests ({count})"
              values={{ count: requestMemberCount }}
              description="Join requests count"
            />
          </h1>
          <p>
            {isPaidWorkspace ? (
              <FormattedMessage
                id="templates.org_members.request-members-billing-subtext"
                defaultMessage="These people have requested to join this Workspace. Adding new Workspace members will automatically update your bill."
                description="Subtext for the access request section for paid workspaces"
              />
            ) : (
              <FormattedMessage
                id="templates.org_members.request-members-subtext-collaborator-limit"
                defaultMessage="These people have requested to join this Workspace. Adding new Workspace members will automatically update your bill. Workspace guests already count toward the free Workspace collaborator limit."
                description="Subtext for the access request section"
              />
            )}
          </p>
          <LazyWorkspaceUserLimitMessage
            location="members"
            workspaceId={orgId}
          />
          <div>
            <div>
              {!requestMemberCount && (
                <div>
                  <hr role="presentation" />
                  <p className={styles.requestMembersEmptyStateText}>
                    <FormattedMessage
                      id="templates.org_members.request-members-no-requests-text"
                      defaultMessage="There are no join requests."
                      description="No join requests message"
                    />
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
